import {
    AuthContext, CreateEligibilityDialog,
    DataTable,
    DataTablePresenter, IEligibilityRequest,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderClaimEligibilitiesRequest,
    ProviderClaimEligibility, ProviderCreateClaimEligibilityPayload, ProviderProvider,
    renderDate,
    renderString, Spinner, useCommonDialogContext,
} from '@enter/core';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, {useContext, useEffect, useRef, useState} from 'react';
import { Row } from 'react-table';
import { DialogContext } from 'services';
import { parseSortBy, RootStore } from 'stores';
import { EligibilityDetail } from '@enter/core';
import * as S from './eligibility.styles';
import {ClaimService} from "../../../stores/claim.service";

const columns: ITableColumn<ProviderClaimEligibility>[] = [
    {
        id: 'Name',
        Header: 'Insured Name',
        accessor: 'first',
        disableSortBy: true,
        Cell: ({ row }) => <S.Cell>{_([row.original.first, row.original.last]).compact().join(' ')}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'PlanID',
        Header: 'Plan ID',
        accessor: 'planId',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderString(cell.value)}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'GroupID',
        Header: 'Group ID',
        accessor: 'groupId',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderString(cell.value)}</S.Cell>,
        minWidth: 250,
    },
    {
        id: 'DOB',
        Header: 'DOB',
        accessor: 'dob',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderDate(cell.value)}</S.Cell>,
        show: false,
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderClaimEligibilitiesRequest>[] = [
    {
        label: 'Claim ID',
        filterId: 'filterClaimId',
        columnId: 'claimId',
        filterType: 'String',
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
    {
        label: 'Created',
        filterId: 'filterTypeParameter',
        filterType: 'String',
        columnId: 'Created',
    },
];

type EligibilityProps = {};

type IInjectedProps = EligibilityProps & { rootStore: RootStore };

export const Eligibility = inject('rootStore')(
    observer((props: EligibilityProps) => {
        const { rootStore } = props as IInjectedProps;
        if (!rootStore) {
            return <></>;
        }

        const [loading, setLoading] = React.useState(false);
        const [submitting, setSubmitting] = useState(false);
        const [billingProvider, setBillingProvider] = useState({} as ProviderProvider);
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const commonDialogContext = useCommonDialogContext(dialogContext);

        const presenter = useRef(
            new DataTablePresenter({
                name: 'Eligibility',
                data: [],
                columns,
                filterSchemas,
            }),
        ).current;

        const onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
            try {
                setLoading(true);
                authContext.cancelGroup('documents');
                authContext.setGroup('documents');

                const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
                const sortAndOrder = parseSortBy(sortBy, columns);
                const listing = await rootStore.eligibilityStore.loadEligibilityListing({
                    page: pageIndex,
                    pageSize,
                    ...sortAndOrder,
                    filters: validFilters,
                });
                presenter.onDataUpdate(listing.eligibilities || []);
                presenter.onDataTotalChange(listing.total || 0);

                setLoading(false);
            } catch (err) {
                dialogContext.showError(err);
            }
        };

        const renderRowSubComponent = (row: Row<ProviderClaimEligibility>): React.ReactElement => {
            return (
                <S.Expand>
                    <EligibilityDetail eligibility={row.original} />
                </S.Expand>
            );
        };

        const onRowClick = async (row: Row<ProviderClaimEligibility>) => {
            row.toggleRowExpanded();
        };

        const displayRunEligibility = async () => {
            if(commonDialogContext) {
                const handleDialogClose = (): void => {
                    console.log("handleDialogClose fired");
                    commonDialogContext.hide();
                };

                const handleSubmit = async (eligibilityRequest: IEligibilityRequest): Promise<string|undefined> => {
                    commonDialogContext.show({
                        props: {
                            renderTitle: 'Running Eligibility...'
                        },
                        Component: (<Spinner/>)
                    });

                    let newEligibility = undefined;

                    try {
                        newEligibility = await rootStore.eligibilityStore.createEligibility(eligibilityRequest as ProviderCreateClaimEligibilityPayload);
                    } catch(e) {
                        commonDialogContext.hide();
                        throw e;
                    }



                    commonDialogContext.show({
                        props: {
                            renderTitle: 'Eligibility Details',
                            showCloseButton: true,
                            onClose: (event: any, detail: string|undefined): void => {
                                console.log("Closing eligibility details");
                                commonDialogContext.hide();
                            }
                        },
                        Component: (<EligibilityDetail
                            eligibility={newEligibility}
                        />)
                    });

                    //Reload
                    await rootStore.eligibilityStore.loadEligibilityListing({});

                    return undefined;
                }

                setSubmitting(true);

                let loadedBillingProvider: ProviderProvider|undefined = (billingProvider && billingProvider.type && billingProvider.type === 'BILLING') ? billingProvider : undefined;

                if(billingProvider && billingProvider.type && billingProvider.type === 'BILLING') {
                    //Use cache
                } else {
                    const providers: ProviderProvider[] = await ClaimService.fetchProviders([{
                        filterProviderType: ['EQ:BILLING']
                    }])

                    if(providers.length > 0) {
                        setBillingProvider(providers[0]);
                        loadedBillingProvider = providers[0];
                    }
                }

                const defaultBillingProviderNpi = loadedBillingProvider ? loadedBillingProvider.npi : undefined;
                const defaultBillingProviderOrg = loadedBillingProvider ? loadedBillingProvider.organization : undefined;
                const providerList = loadedBillingProvider ? [loadedBillingProvider] : [];

                commonDialogContext.show({
                    props: {
                        renderTitle: 'Run Eligibility',
                        expandable: true
                    },
                    Component: (<CreateEligibilityDialog
                        providers={providerList}
                        defaultProviderNpi={defaultBillingProviderNpi}
                        defaultProviderOrgName={defaultBillingProviderOrg}
                        onClose={handleDialogClose}
                        onSubmit={handleSubmit}
                        clearinghousePayers={[]}
                    />)
                });

                setSubmitting(false);
            }
        };

        useEffect(() => {
            presenter.onDataUpdate(rootStore.eligibilityStore.eligibilities);
        }, [presenter, rootStore.eligibilityStore.eligibilities]);

        return (
            <PageLayout>
                <S.TitleWrapperRow>
                    <S.TitleWrapper>
                        <S.Title>Eligiblity</S.Title>
                        <S.MenuButton onClick={displayRunEligibility} disabled={submitting}>{submitting ? <Spinner/> : <>Run Eligibility</>}</S.MenuButton>
                    </S.TitleWrapper>
                </S.TitleWrapperRow>
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={presenter}
                            isLoading={loading}
                            onFetch={onFetch}
                            onRowClick={onRowClick}
                            renderRowSubComponent={renderRowSubComponent}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }),
);
